import { FreeBetVoucherConditionInfo, Labels, VoucherInfo, VoucherType, PromotionType, ValidationMode, VoucherConditionMode } from '@/types';
import type { VoucherDetailInfo } from '../../types';
import { siteService } from '@/services/site.service';
import { voucherService } from '../../services/voucher.service';
import clsx from 'clsx';

import { useReward } from '@/contexts/reward-context';
import { CloseIcon } from '@/components/svg-icons';
import { EventVoucherIcon } from '@/components/icons/event-voucher-icon';
import { RewardModalWrapper } from '@/components/reward-modal-wrapper';
import { extractDateParts, formatExpireDateWithTime } from '@/utils/date';

interface VoucherDetailModalProps {
	voucher: VoucherInfo;
	voucherDetailInfo: VoucherDetailInfo;
	voucherConditionInfo?: FreeBetVoucherConditionInfo | null;
	onClose: () => void;
}

const getGiftPromotionTagLabel = (
	voucherType: number, 
	labels?: Labels, 
	promotionInfo?: {
		Id: number;
		ResourceKey: string | null;
		ResourceName: string | null;
		ShowHint: boolean;
		PromotionType: number;
	}
) => {
	switch (voucherType) {
		case VoucherType.GiftRiskFreeBet:
			return labels?.lbl_IDRRFMyCoupon;
		case VoucherType.FreeBet:
			return promotionInfo?.ResourceName || '';
		default:
			return labels?.lbl_giveaway;
	}
};

const formatOdds = (odds?: number | null) => (odds == null ? null : String(odds));

export const VoucherDetailModal = ({
	voucher,
	voucherDetailInfo,
	voucherConditionInfo,
	onClose
}: VoucherDetailModalProps) => {
	const { labels, siteId, currencyName } = useReward();
	const isSm88 = siteService.isSm88(siteId);

	const isCustomizedFreeBet = voucher.VoucherType === VoucherType.FreeBet &&
	voucher.PromotionInfo?.PromotionType === PromotionType.Customized;

	const isFreeBet = voucher.VoucherType === VoucherType.FreeBet;
	
	const expireDate = formatExpireDateWithTime(voucher.ExpireDate);
	const validityPeriod = voucher.EffectiveDate
		? `${formatExpireDateWithTime(voucher.EffectiveDate).replace(/\s*\(GMT-4\)$/, '')} – ${expireDate}`
		: expireDate;
	const condition = voucherConditionInfo?.Condition;
	const sportConditions = voucherConditionInfo?.ConditionMode === VoucherConditionMode.SpecificSports
		? condition?.Sports
		: undefined;
	const conditionDetail = sportConditions ? null : condition;
	const minOdds = formatOdds(condition?.OddsLimitRule?.MinOdds);
	const maxOdds = formatOdds(condition?.OddsLimitRule?.MaxOdds);
	const sportsNames = [...new Set(conditionDetail?.SportsName ?? [])].join(',');
	const leagueNames = conditionDetail?.LeaguesName ?? [];
	
	const betTypeNames = conditionDetail?.BetTypesName 
		? conditionDetail.BetTypesName.join(', ')
		: null;

	
	return (
		<RewardModalWrapper>
			<div className="shop-voucher-modal">
				<div 
					className="shop-voucher-modal__close" 
					onClick={onClose}
				>
					<CloseIcon />
				</div>
				<div className="shop-voucher-modal__main">
					<div className="shop-voucher-detail">
						<div className="shop-voucher-detail__header">
							<span className="shop-voucher-detail__title">
								{voucherDetailInfo.title}
							</span>
							<div className="reward-promotion">
								{voucherDetailInfo.isGift ? (
									<div className={clsx(
										'reward-promotion__tag',
										{ 'reward-promotion__tag--customized': isCustomizedFreeBet }
									)}>
										{getGiftPromotionTagLabel(voucher.VoucherType, labels, voucher.PromotionInfo)}
									</div>
								) : null}
							</div>
						</div>
						<div className="shop-voucher-detail__body reward-scroller">
							{voucher.VoucherType === VoucherType.FreeOddsBooster || voucher.VoucherType === VoucherType.AffPromotionOddsBoost ? (
								<>
									<h4>{labels?.lbl_RPVoucherDetails}</h4>
									<p>{labels?.lbl_obpdetails01}</p>
									<h4>{labels?.lbl_RPUsageRules}</h4>
									<ol>
										<li>{labels?.lbl_obpdetails02}</li>
										<li>{labels?.lbl_obpdetails03}</li>
										<li>{labels?.lbl_obpdetails04}</li>
										<li>{isSm88 ? labels?.lbl_DebetObp07 : labels?.lbl_obpdetails05}</li>
										<li>{labels?.lbl_obpdetails06}</li>
										<li>{labels?.lbl_obpdetails07}</li>
										<li>{labels?.lbl_obpdetails08}</li>
										<li>{labels?.lbl_obpdetails09}</li>
										<li>{labels?.lbl_obpdetails10}</li>
										<li>{labels?.lbl_obpdetails11}</li>
									</ol>
								</>
							): null }

							{voucher.VoucherType === VoucherType.GiftRiskFreeBet ? (
								<>
									<h4>{labels?.lbl_RPVoucherDetails}</h4>
									<p>{labels?.lbl_RPRiskFreeDetails.replace('{X}', String(voucher.Quota))}</p>
									<h4>{labels?.lbl_RPUsageRules}</h4>
									<ol>
										<li>{labels?.lbl_IDRRFVoucherRules01}</li>
										<li>{labels?.lbl_IDRRFVoucherRules02}</li>
										<li>{labels?.lbl_RPVoucherRules5}</li>
										<li>{labels?.lbl_RPVoucherRules6}</li>
										<li>{labels?.lbl_RPVoucherRules7}</li>
										<li>{labels?.lbl_RPVoucherRules8}</li>
										<li>{labels?.lbl_RPVoucherRules9}</li>
									</ol>
								</>
							): null }

							{isFreeBet ? (
								<>
									<h4>{labels?.lbl_RPVoucherDetails}</h4>
									<p>{labels?.lbl_FBInfo02?.replace('{Amount}', String(voucher.Quota)).replace('{Currency}', currencyName || '')}</p>
									<h4>{labels?.lbl_RPUsageRules}</h4>
									<ol>
										<li>{labels?.lbl_FBRR11} {validityPeriod}</li>
										{maxOdds && (
											<li>{labels?.lbl_FBRR12} {maxOdds}({labels?.lbl_OddsTypeMin1}) <br/>
												{labels?.lbl_FBRR14}
											</li>
										)}
										{voucherConditionInfo ? (
											minOdds && (
												<li>{labels?.lbl_FBRR13} {minOdds}({labels?.lbl_OddsTypeMin1}) <br/>
													{labels?.lbl_FBRR14}
												</li>
											)
										) : (
											<li>{labels?.lbl_FBRR13} 1.5({labels?.lbl_OddsTypeMin1}) <br/>
												{labels?.lbl_FBRR14}
											</li>
										)}
										{sportsNames && (
											<li>
												{conditionDetail?.SportMode === ValidationMode.ALLOW ? labels?.lbl_FBRR15 : labels?.lbl_FBRR19} {sportsNames}
											</li>
										)}
										{leagueNames && leagueNames.length > 0 && (
											<li>
												{conditionDetail?.LeagueIdMode === ValidationMode.ALLOW ? labels?.lbl_FBRR16 : labels?.lbl_FBRR20}
												<ul>
													{leagueNames.map((leagueName, index) => (
														<li key={`${leagueName}-${index}`}>{leagueName}</li>
													))}
												</ul>
											</li>
										)}
										{betTypeNames && (
											<li>
												{conditionDetail?.BetTypeMode === ValidationMode.ALLOW ? labels?.lbl_FBRR17 : labels?.lbl_FBRR21} {betTypeNames}
											</li>
										)}
										{sportConditions && sportConditions.length > 0 && (
											<li>
												{labels?.lbl_FBRR15}
												<ul>
													{sportConditions.map((sport, sportIndex) => (
														<li key={`${sport.SportName}-${sportIndex}`}>
															{sport.SportName}
															{!sport.AllLeagues && (
																<>
																	<br/>
																	{labels?.lbl_FBRR16}
																	<ul>
																		{sport.LeaguesName.map((leagueName, index) => (
																			<li key={`${leagueName}-${index}`}>{leagueName}</li>
																		))}
																	</ul>
																</>
															)}
															{(sport.BetTypesMode === ValidationMode.DENY ? sport.BetTypesName.length > 0 : !sport.AllBetTypes) && (
																<>
																	{sport.AllLeagues && <br/>}
																	{sport.BetTypesMode === ValidationMode.DENY ? labels?.lbl_FBRR21 : labels?.lbl_FBRR17} {sport.BetTypesName.join(', ')}
																</>
															)}
														</li>
													))}
												</ul>
											</li>
										)}
										<li>{labels?.lbl_FBRR03}</li>
										<li>{labels?.lbl_FBRR04}</li>
										<li>{labels?.lbl_FBRR18}</li>
										<li>{labels?.lbl_FBRR05}</li>
										<li>{labels?.lbl_FBRR06}</li>
										<li>{labels?.lbl_FBRR07}</li>
										<li>{labels?.lbl_FBRR08}</li>
										<li>{labels?.lbl_FBRR09}</li>
										<li>{labels?.lbl_FBRR10}</li>
									</ol>
								</>
							): null }
						</div>
					</div>
					<div className="shop-voucher-icon">
						<EventVoucherIcon 
							voucherType={voucher.VoucherType} 
							isGift={voucherService.isGiftVoucher(voucher)}
							promotionInfo={voucher.PromotionInfo}
						/>
					</div>
				</div>
			</div>
		</RewardModalWrapper>
	);
};